import React from "react";
import { AcmeCoOperationErrorIcon } from "../../assets/icons";
import { useSelector } from "react-redux";

const ReviewCreate = ({
  formValues,
  modulesList = [],
  plansList = [],
  countriesList = [],
}) => {
  const staffList = useSelector((state) => state.staff.staffList);
  const getCountryName = (countryId) => {
    if (!countryId) return "Not provided";
    return (
      countriesList.find((c) => c.id === countryId)?.name || "Not provided"
    );
  };

  // Helper function to get display value
  const getDisplayValue = (value) => {
    if (value === null || value === undefined || value === "")
      return "Not provided";
    if (typeof value === "object" && value !== null) {
      return value.label || "Not provided";
    }
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(", ") : "None selected";
    }
    return value;
  };

  const getStaffName = (staffId) => {
    if (!staffId) return "Not provided";
    return staffList.find((s) => s.id === staffId)?.name || "Not provided";
  };
  const getModuleNames = (moduleIds) => {
    if (!moduleIds || moduleIds.length === 0) return "None selected";

    return moduleIds
      .map((module) => {
        const id = typeof module === "object" ? module.id : module;
        return modulesList.find((mod) => mod.id === id)?.name;
      })
      .filter(Boolean)
      .join(", ");
  };

  // Get plan name from ID
  const getPlanName = (planId) => {
    return plansList.find((plan) => plan.id === planId)?.name || "Not selected";
  };

  // Get billing cycle label
  const getBillingCycleLabel = (cycle) => {
    const cycles = {
      monthly: "Monthly",
      quarterly: "Quarterly",
      annually: "Annually",
    };
    return cycles[cycle] || "Not selected";
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#EDF7FF] rounded-xl sm:p-6 p-3">
        <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between">
          <div className="flex items-center gap-3">
            <span className="min-[40px]">{AcmeCoOperationErrorIcon}</span>
            <div>
              <span className="text-[16px] font-semibold mb-2 block">
                Review Tenant configuration
              </span>
              <p className="text-[16px] text-gray-500 text-sm mb-0">
                Please review the details below before creating the tenant
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Company Information */}
      <section className="chart-card">
        <h2 className="text-span text-black !font-semibold mb-0">
          Company Information
        </h2>
        <div className="mt-3 flex items-start justify-between flex-wrap gap-2">
          <dl className="space-y-2 mb-0">
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Company Name
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getDisplayValue(formValues.companyName)}
              </dd>
            </div>
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Email Address
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getDisplayValue(formValues.companyEmail)}
              </dd>
            </div>
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Employees
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getDisplayValue(formValues.employeesCount)}
              </dd>
            </div>
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Company Address
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getDisplayValue(formValues.companyAddress)}
              </dd>
            </div>
          </dl>

          <dl className="space-y-2 mb-0">
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Industry
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getDisplayValue(formValues.industry)}
              </dd>
            </div>
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Phone
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getDisplayValue(formValues.phoneNumber)}
              </dd>
            </div>
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Country
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getCountryName(formValues.country)}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Administrator Information */}
      <section className="chart-card">
        <h2 className="text-span text-black !font-semibold mb-0">
          Administrator
        </h2>
        <div className="mt-3 flex items-start justify-between flex-wrap gap-2">
          <dl className="space-y-2 mb-0">
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Name
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getDisplayValue(formValues.adminFirstName)}{" "}
                {getDisplayValue(formValues.adminLastName)}
              </dd>
            </div>
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Email Address
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getDisplayValue(formValues.adminEmail)}
              </dd>
            </div>
          </dl>

          <dl className="space-y-2 mb-0">
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Phone
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getDisplayValue(formValues.adminPhone)}
              </dd>
            </div>
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Password Reset Required
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getDisplayValue(formValues.requirePasswordReset)}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Plan & Billing */}
      <section className="chart-card">
        <h2 className="text-span text-black !font-semibold mb-0">
          Plan & Billing
        </h2>
        <div className="mt-3 flex items-start justify-between flex-wrap gap-2">
          <dl className="space-y-2 mb-0">
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Plan
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getPlanName(formValues.selectedPlan)}
              </dd>
            </div>
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Modules
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getModuleNames(formValues.enabledModules)}
              </dd>
            </div>
          </dl>

          <dl className="space-y-2 mb-0">
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Billing Cycle
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getBillingCycleLabel(formValues.billingCycle)}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Configuration */}
      <section className="chart-card">
        <h2 className="text-span text-black !font-semibold mb-0">
          Configuration
        </h2>
        <div className="mt-3 flex items-start justify-between flex-wrap gap-2">
          <dl className="space-y-2 mb-0">
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Support Owner
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getStaffName(formValues.supportOwner)}
              </dd>
            </div>
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Onboarding Specialist
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getStaffName(formValues.onboardingSpecialist)}
              </dd>
            </div>
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Data Retention Period
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getDisplayValue(formValues.dataRetentionPeriod)} days
              </dd>
            </div>
          </dl>

          <dl className="space-y-2 mb-0">
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                SSO Enabled
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getDisplayValue(formValues.enableSSO)}
              </dd>
            </div>
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                API Access
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getDisplayValue(formValues.enableAPI)}
              </dd>
            </div>
            <div className="flex items-start gap-1">
              <dt className="text-span text-black !font-semibold mb-0 after:content-[':']">
                Custom Branding
              </dt>
              <dd className="text-span text-[#888076] mb-0">
                {getDisplayValue(formValues.enableBranding)}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Internal Notes */}
      {formValues.internalNotes && (
        <section className="chart-card">
          <h2 className="text-span text-black !font-semibold mb-0">
            Internal Notes
          </h2>
          <div className="mt-3">
            <p className="text-span text-[#888076] mb-0">
              {formValues.internalNotes}
            </p>
          </div>
        </section>
      )}
    </div>
  );
};

export default ReviewCreate;
